import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, Card, withStyles, InboundETransferList, capitalizeFirstLetter } from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: InboundETransferList;
  isParent?: boolean;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const InboundETransferTransactionInfo: FC<TransactionInfoProps> = ({ transaction, isParent = false }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InboundETransferTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.Status}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.Status}`, {
                defaultValue: transaction.Status,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('TransactionDetails.inboundETransfer'),
    },
    {
      label: t('TransactionDetails.dateReceived', { defaultValue: 'Date Received' }),
      value: transaction.DateReceived,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `$${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.message', { defaultValue: 'Message' }),
      value: transaction.Message || '-',
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state.failureReason || '-',
      visible: isParent && state.failureReason !== '',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.DateReceived,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `$${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: !isParent,
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.Name}
          amount={transaction.Amount}
          currency={'CAD'}
          transactionId={transaction.ID}
          type={'Inbound e-Transfer'}
          failureReason={state?.failureReason}
          contactId={state?.accountId}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default InboundETransferTransactionInfo;
